import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { useParams } from 'react-router-dom';
import { Campaign } from 'src/services/campaigns/campaigns.interface';
import { useCampaigns } from 'src/hooks/useCampaigns';
import { useNotification } from 'src/contexts/NotificationContext';

interface CampaignContextValue {
  campaign: Campaign | null;
  isLoadingCampaign: boolean;
  isInitializing: boolean;
  campaignReferenceId: string | undefined;

  loadCampaign: () => Promise<void>;
  setCampaign: (campaign: Campaign | null) => void;
}

const CampaignContext = createContext<CampaignContextValue | undefined>(
  undefined,
);

export const useCampaignContext = () => {
  const context = useContext(CampaignContext);
  if (context === undefined) {
    throw new Error(
      'useCampaignContext must be used within a CampaignProvider',
    );
  }
  return context;
};

interface CampaignProviderProps {
  children: ReactNode;
  campaignId?: string;
}

export const CampaignProvider: React.FC<CampaignProviderProps> = ({
  children,
  campaignId: propCampaignId,
}) => {
  const { id: urlCampaignId } = useParams();
  const campaignReferenceId = propCampaignId || urlCampaignId;
  const campaignsService = useCampaigns();
  const notification = useNotification();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(false);

  const loadCampaign = async () => {
    if (!campaignReferenceId) {
      setIsLoadingCampaign(false);
      return;
    }

    try {
      setIsLoadingCampaign(true);
      const loadedCampaign =
        await campaignsService.fetchCampaign(campaignReferenceId);
      setCampaign(loadedCampaign);
    } catch (error) {
      console.error('Failed to load campaign:', error);
      notification.error('Failed to load campaign details');
    } finally {
      setIsLoadingCampaign(false);
    }
  };

  useEffect(() => {
    loadCampaign();
  }, [campaignReferenceId]);

  return (
    <CampaignContext.Provider
      value={{
        campaign,
        isLoadingCampaign,
        isInitializing: isLoadingCampaign,
        campaignReferenceId,

        loadCampaign,
        setCampaign,
      }}
    >
      {children}
    </CampaignContext.Provider>
  );
};
